import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toFieldErrors } from '@/shared/api/submit';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { findServices, createBidReview } from '../detailApi';
import { BID_STATUS } from '@/shared/constants/bids';

type Props = {
  bidId: number;
  customerId: number | string;
  serviceId?: number | string | null;
  statusId?: number;
};

const RATING_FIELDS = [
  { key: 'locationRate', label: 'Оценка локации сервиса' },
  { key: 'qualificationRate', label: 'Оценка квалификации сотрудников' },
  { key: 'appearanceRate', label: 'Оценка внешнего вида' },
  { key: 'qualityRate', label: 'Оценка ремонта' },
  { key: 'finalRate', label: 'Итоговая оценка' },
] as const;

type RatingKey = (typeof RATING_FIELDS)[number]['key'];

const StarRow = ({ value, onChange }: { value: number; onChange: (n: number) => void }) => (
  <div style={{ display: 'flex', gap: 4 }}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        className={`btn btn-xs ${value >= n ? 'btn-warning' : 'btn-default'}`}
        onClick={() => onChange(n)}
      >
        ★
      </button>
    ))}
  </div>
);

export const CreateReviewForm = ({ bidId, customerId, serviceId, statusId }: Props) => {
  const isCancelled = Number(statusId) === BID_STATUS.CANCELLED;
  const [text, setText] = useState('');
  const [pickedServiceId, setPickedServiceId] = useState('');
  const [serviceQuery, setServiceQuery] = useState('');
  const [serviceOptions, setServiceOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [ratings, setRatings] = useState<Record<RatingKey, number>>({
    locationRate: 0,
    qualificationRate: 0,
    appearanceRate: 0,
    qualityRate: 0,
    finalRate: 0,
  });
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const searchServices = useMutation({
    mutationFn: () => findServices(serviceQuery),
    onSuccess: (rows) =>
      setServiceOptions(rows.map((s) => ({ value: String(s.id), label: `${s.id} — ${s.name ?? ''}` }))),
  });

  const save = useMutation({
    mutationFn: () =>
      createBidReview({
        bidId,
        customerId,
        serviceId: isCancelled ? pickedServiceId : serviceId ?? undefined,
        text,
        ...ratings,
      }),
    onSuccess: () => {
      setSaved(true);
      setError('');
    },
    onError: (err) => {
      setSaved(false);
      const fieldErrors = toFieldErrors(err);
      setError(Object.values(fieldErrors).flat().join('; ') || 'Ошибка сохранения отзыва');
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (RATING_FIELDS.some(({ key }) => !ratings[key])) {
      setError('Заполните все поля рейтинга');
      return;
    }
    if (isCancelled && !pickedServiceId) {
      setError('Выберите сервис');
      return;
    }
    save.mutate();
  };

  return (
    <form onSubmit={onSubmit}>
      {isCancelled && (
        <div className="form-group">
          <label className="control-label">Сервис</label>
          <div className="input-group" style={{ maxWidth: 480, marginBottom: 8 }}>
            <input
              className="form-control"
              placeholder="id или название СТО..."
              value={serviceQuery}
              onChange={(e) => setServiceQuery(e.target.value)}
            />
            <span className="input-group-btn">
              <button
                type="button"
                className="btn btn-default"
                disabled={!serviceQuery.trim() || searchServices.isPending}
                onClick={() => searchServices.mutate()}
              >
                Найти
              </button>
            </span>
          </div>
          <FilterSelect
            options={serviceOptions}
            value={pickedServiceId}
            width={480}
            placeholder="Выберите СТО..."
            onChange={setPickedServiceId}
          />
        </div>
      )}
      <div className="form-group">
        <label className="control-label">Введите текст отзыва</label>
        <textarea className="form-control" required value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <div className="row">
        {RATING_FIELDS.map(({ key, label }) => (
          <div className="col-md-2" key={key} style={{ marginBottom: 12 }}>
            <b>{label}:</b>
            <StarRow value={ratings[key]} onChange={(n) => setRatings((prev) => ({ ...prev, [key]: n }))} />
          </div>
        ))}
      </div>
      <button type="submit" className="btn btn-primary" disabled={save.isPending}>
        Отправить
      </button>
      {saved && <span className="label label-success" style={{ marginLeft: 8 }}>Успех</span>}
      {error && <span className="label label-danger" style={{ marginLeft: 8 }}>{error}</span>}
    </form>
  );
};
